<template>
    <v-card>
        <v-card-title>
            <v-row>
                <v-col cols="4">
                    <search v-model="search"/>
                </v-col>
                <v-col cols="3">
                    <v-checkbox v-model="showOnlyNhvas">
                        <template v-slot:label>Show NHVAS units only</template>
                    </v-checkbox>
                </v-col>
                <v-col cols="3">
                    <v-checkbox v-model="showOnlyDeleted" v-if="userIsFleetManager">
                        <template v-slot:label>Show Deleted units only</template>
                    </v-checkbox>
                </v-col>
                <v-col cols="2">
                    <v-menu class="pr-n2">
                        <template v-slot:activator="{ on }">
                            <v-btn small tile v-on="on" class="mt-4">
                                <v-icon>save_alt</v-icon>&nbsp;Export
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item @click="exportCsv()">
                                <v-list-item-title>
                                    <v-icon>grid_on</v-icon>&nbsp;Excel (CSV)
                                    <v-chip x-small>limit {{maxRowsPerCsv}} rows</v-chip>
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-col>
            </v-row>
        </v-card-title>
        <v-data-table
            :items="units.data"
            :items-per-page="options.itemsPerPage"
            :headers="headers"
            :loading="$wait.is(['loading units'])"
            :footer-props.sync="footerProps"
            :server-items-length="units.total"
            :options.sync="options"
        >
            <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
            <template v-slot:item="props">
                <tr @click="goTo(props.item.id)">
                    <td>{{props.item.number}}</td>
                    <td>{{props.item.name}}</td>
                    <td>{{props.item.department}}</td>
                    <td>{{props.item.garage}}</td>
                    <td>{{props.item.registrationNumber}}</td>
                    <td>{{props.item.chassisConfiguration}}</td>
                    <td>{{props.item.bodyConfiguration}}</td>
                    <td v-if="props.item.brakeCheck">
                        <span>{{brakeTypes[props.item.brakeCheck.brakeType]}} ({{props.item.brakeCheck.slackType}})</span>
                        <br/>
                        <span
                            class="caption"
                            v-if="props.item.brakeCheck.exemptionType != 'NotExempt'"
                        >{{exemptionTypes[props.item.brakeCheck.exemptionType]}}</span>
                        <span
                            class="caption"
                            v-else
                        >{{schedules[props.item.brakeCheck.brakeCheckSchedule]}}</span>
                    </td>
                    <td v-else></td>
                    <td class='center'>{{props.item.daysSinceLastMetrics}}</td>
                </tr>
            </template>
            <template v-slot:no-results>
                <v-layout row align-center>
                    <v-flex>No matching records found</v-flex>
                </v-layout>
            </template>
        </v-data-table>
    </v-card>
</template>

<script>
  import moment from 'moment';
  import {DateFormat} from '@/plugins/date-filter';
  import Search from '../Search.vue';
  import Units from './UnitsClient.vue';
  import DownloadHelper from '@/lib/DownloadHelper';
  import BrakeCheckHelper from '@/lib/BrakeCheckHelper';
  import ConfigurationHelper from '@/lib/ConfigurationHelper';

  export default {
    components: {
      Search
    },
    mixins: [Units],
    data() {
      return {
        units: [],
        dialog: false,
        search: this.$route.query.search || '',
        loading: true,
        showOnlyNhvas: false,
        showOnlyDeleted: false,
        maxRowsPerCsv: 6000,
        searchParams: null,
        options: {
          itemsPerPage: 50,
          sortBy: ['name']
        },
        footerProps: {
          itemsPerPageOptions: [10, 50, 100]
        },
        headers: [
          {text: 'Number', value: 'number'},
          {text: 'Name', value: 'name'},
          {text: 'Department', value: 'department'},
          {text: 'Garage Address', value: 'garage'},
          {text: 'Registration', value: 'registrationNumber'},
          {text: 'Chassis', value: 'chassisConfiguration'},
          {text: 'Body', value: 'bodyConfiguration'},
          {text: 'Brake Check', value: 'brakeType', sortable: false},
          {text: 'Days Since Last Metrics', value: 'daysSinceLastMetrics'}
        ],
        model: {},
        brakeTypes: BrakeCheckHelper.brakeTypes,
        exemptionTypes: BrakeCheckHelper.exemptionTypes,
        schedules: BrakeCheckHelper.schedules,
        configurationHelper: ConfigurationHelper,
      };
    },
    computed: {
      params() {
        return {
          pageSize: this.options && this.options.itemsPerPage,
          page: this.options && this.options.page,
          searchTerm: this.search,
          onlyNhvas: this.showOnlyNhvas,
          onlyDeleted: this.showOnlyDeleted,
          orderBy: this.orderBy,
          unitType: 'vehicle'
        };
      },
      routeQuery() {
        if (this.search) {
          return {search: this.search};
        }
      },
      orderBy() {
        if (this.options.sortBy.length) {
          if (this.options.sortDesc && this.options.sortDesc.length && this.options.sortDesc[0]) {
            return `-${this.options.sortBy[0]}`;
          }
          return this.options.sortBy[0];
        }

        return '';
      }
    },
    methods: {
      goTo(id) {
        this.$router.push({name: 'unit', params: {unitId: id}});
      },
      async exportCsv() {
        let searchParams = this.getSearchParams();
        searchParams.page = 1;
        searchParams.pageSize = this.maxRowsPerCsv;
        const results = await this.getUnitsUsingQuery(searchParams);
        const data = results ? results.data : [];
        const rows = data.map(x => {
          let brakeCheck = '';
          if (x.brakeCheck) {
            brakeCheck = `${x.brakeCheck.brakeType} (${x.brakeCheck.slackType})`;
            if (x.brakeCheck.exemptionType != 'NotExempt') {
              brakeCheck = `${brakeCheck} ${this.exemptionTypes[x.brakeCheck.exemptionType]}`;
            } else {
              brakeCheck = `${brakeCheck} ${this.schedules[x.brakeCheck.brakeCheckSchedule]}`;
            }
          }

          return [
            x.unitMasterKey,
            x.name,
            x.truckType,
            x.department,
            x.activityName,
            x.vin,
            x.engineNumber,
            x.registrationNumber,
            x.garage,
            x.isNhvasMaintenanceAccredited,
            x.isLowUse,
            x.installationAddress,
            x.chassisConfiguration,
            x.bodyConfiguration,
            brakeCheck,
            x.daysSinceLastMetrics
          ];
        });
        const csv = DownloadHelper.makeCsv([
          'Unit',
          'Unit Name',
          'Truck Type',
          'Department',
          'Activity Name',
          'VIN',
          'Engine #',
          'Registration',
          'Garage',
          'Is NHVAS',
          'Is Low Use',
          'Installation Address',
          'Chassis',
          'Body',
          'Brake Check',
          'Days Since Last Metrics'
        ], rows);
        DownloadHelper.download(`Units_${moment().format(DateFormat.Canonical)}.csv`, new Blob([csv]));
      },
      getSearchParams() {
        const {sortBy, sortDesc, page, itemsPerPage} = this.options;

        return {
          searchTerm: this.search,
          onlyNhvas: this.showOnlyNhvas,
          onlyDeleted: this.showOnlyDeleted,
          unitType: 'vehicle',
          page,
          pageSize: itemsPerPage,
          orderBy: sortBy.length ? sortBy[0] : null,
          orderByDescending: sortDesc.length ? sortDesc[0] : false
        };
      }
    },
    watch: {
      params: {
        async handler() {
          this.units = await this.getUnits();
        },
        deep: true
      },
      search(val) {
        this.$router.push({path: 'units', query: this.routeQuery});
      }
    },
  };
</script>
<style>
    td {
        cursor: pointer;
    }
    td.center {
      text-align: center !important;
    }
</style>
